package com.evoapps.evoshift.ui.roster

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ExpandLess
import androidx.compose.material.icons.filled.ExpandMore
import androidx.compose.material.icons.filled.Groups
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.PlainTooltip
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TooltipAnchorPosition
import androidx.compose.material3.TooltipBox
import androidx.compose.material3.TooltipDefaults
import androidx.compose.material3.rememberTooltipState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.selected
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.AvailabilitySlot
import com.evoapps.evoshift.domain.model.DayPreference
import com.evoapps.evoshift.domain.schedule.DayAnswer
import com.evoapps.evoshift.domain.schedule.DayAnswerSummary
import com.evoapps.evoshift.domain.schedule.PersonDayAnswer
import com.evoapps.evoshift.domain.schedule.SlotAnswers
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.theme.motionContentSize
import com.evoapps.evoshift.ui.util.label
import com.evoapps.evoshift.widgets.LocalOnColours
import com.evoapps.evoshift.widgets.UserAvatar
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.answers_filter_all
import evoshift.composeapp.generated.resources.answers_group_available
import evoshift.composeapp.generated.resources.answers_group_empty
import evoshift.composeapp.generated.resources.answers_group_no_answer
import evoshift.composeapp.generated.resources.answers_group_unavailable
import evoshift.composeapp.generated.resources.answers_hide_people
import evoshift.composeapp.generated.resources.answers_no_answer
import evoshift.composeapp.generated.resources.answers_over_demand
import evoshift.composeapp.generated.resources.answers_show_people
import evoshift.composeapp.generated.resources.answers_slot_only_other
import evoshift.composeapp.generated.resources.answers_title
import evoshift.composeapp.generated.resources.answers_whole_day
import evoshift.composeapp.generated.resources.roster_picker_absent
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource

/** SH-172: test tags of a day's answers panel. */
object DayAnswersTags {
    const val PANEL = "day_answers"
    const val TOGGLE = "day_answers_toggle"
    const val NO_ANSWER = "day_answers_no_answer"
    const val ALL = "day_answers_all"
    fun slot(slotId: String) = "day_answers_slot_$slotId"
    fun warning(slotId: String) = "day_answers_warning_$slotId"
    fun group(answer: DayAnswer) = "day_answers_group_${answer.name}"
    fun person(userId: String) = "day_answers_person_$userId"
}

/**
 * SH-172: the team's answers for one day while the demand is built (the skeleton editor) or people
 * are assigned (the roster builder's day): each availability slot with how many can work then
 * ("Rano 4 · Popołudnie 2 · Wieczór 1"), "brak odpowiedzi: N", a gentle line for a slot whose
 * planned shifts outnumber those available, and who they are: tap a slot (or "Pokaż osoby") for
 * the names, grouped Dostępni / Brak odpowiedzi / Niedostępni with each one's slot, hours and
 * comment; on a desktop, hovering a slot names its people. [expanded] true keeps the list open
 * (a wide window's side column). Its colours follow the surface ([LocalOnColours]).
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun DayAnswersPanel(
    summary: DayAnswerSummary,
    modifier: Modifier = Modifier,
    expanded: Boolean = false
) {
    val on = LocalOnColours.current
    var open by remember(summary.date) { mutableStateOf(false) }
    var slotFilter by remember(summary.date) { mutableStateOf<String?>(null) }
    val showsList = expanded || open
    Column(
        modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(on.onSurface.copy(alpha = 0.05f))
            .padding(12.dp)
            .motionContentSize()
            .testTag(DayAnswersTags.PANEL),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Icon(Icons.Default.Groups, null, tint = on.onSurface, modifier = Modifier.size(18.dp))
            Spacer(Modifier.width(8.dp))
            Text(
                stringResource(Res.string.answers_title),
                style = MaterialTheme.typography.titleSmall,
                fontWeight = FontWeight.Bold,
                color = on.onSurface,
                modifier = Modifier.weight(1f)
            )
            if (!expanded) {
                TextButton(
                    onClick = { open = !open; if (!open) slotFilter = null },
                    modifier = Modifier.handCursor().testTag(DayAnswersTags.TOGGLE)
                ) {
                    Text(
                        stringResource(if (open) Res.string.answers_hide_people else Res.string.answers_show_people),
                        color = on.link,
                        fontWeight = FontWeight.Bold
                    )
                    Icon(if (open) Icons.Default.ExpandLess else Icons.Default.ExpandMore, null, tint = on.link, modifier = Modifier.size(18.dp))
                }
            }
        }
        FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            if (showsList && summary.slots.isNotEmpty()) {
                AnswerChip(
                    text = stringResource(Res.string.answers_filter_all),
                    selected = slotFilter == null,
                    warn = false,
                    onClick = { slotFilter = null },
                    modifier = Modifier.testTag(DayAnswersTags.ALL)
                )
            }
            summary.slots.forEach { slot ->
                SlotChip(slot, summary, selected = showsList && slotFilter == slot.slot.id) {
                    if (showsList && slotFilter == slot.slot.id) {
                        slotFilter = null
                    } else {
                        slotFilter = slot.slot.id
                        open = true
                    }
                }
            }
            Text(
                stringResource(Res.string.answers_no_answer, summary.noAnswer),
                style = MaterialTheme.typography.labelLarge,
                color = on.onSurfaceVariant,
                modifier = Modifier.align(Alignment.CenterVertically).padding(vertical = 6.dp).testTag(DayAnswersTags.NO_ANSWER)
            )
        }
        summary.warnings.forEach { warning ->
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.testTag(DayAnswersTags.warning(warning.slot.id))) {
                Icon(Icons.Default.Warning, null, tint = on.warning, modifier = Modifier.size(16.dp))
                Spacer(Modifier.width(6.dp))
                Text(
                    pluralStringResource(Res.plurals.answers_over_demand, warning.planned, warning.planned, warning.slot.label, warning.available),
                    style = MaterialTheme.typography.bodySmall,
                    color = on.warning,
                    fontWeight = FontWeight.Bold
                )
            }
        }
        if (showsList) {
            val slots = summary.slots.map { it.slot }
            summary.groups(slotFilter).forEach { (answer, people) ->
                AnswerGroup(answer, people, slots, slotFilter)
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun SlotChip(slot: SlotAnswers, summary: DayAnswerSummary, selected: Boolean, onClick: () -> Unit) {
    val names = summary.groups(slot.slot.id)[DayAnswer.AVAILABLE].orEmpty().joinToString(", ") { it.user.displayName }
    // A desktop's pointer over the slot names its people; a tap filters the list to them.
    TooltipBox(
        positionProvider = TooltipDefaults.rememberTooltipPositionProvider(TooltipAnchorPosition.Above),
        tooltip = { PlainTooltip { Text(names.ifEmpty { stringResource(Res.string.answers_group_empty) }) } },
        state = rememberTooltipState()
    ) {
        AnswerChip(
            text = "${slot.slot.label} ${slot.available}",
            selected = selected,
            warn = slot.overDemand,
            onClick = onClick,
            modifier = Modifier.testTag(DayAnswersTags.slot(slot.slot.id))
        )
    }
}

@Composable
private fun AnswerChip(text: String, selected: Boolean, warn: Boolean, onClick: () -> Unit, modifier: Modifier = Modifier) {
    val on = LocalOnColours.current
    val tint = if (warn) on.warning else on.onSurface
    Row(
        modifier
            .heightIn(min = 36.dp)
            .clip(RoundedCornerShape(18.dp))
            .background(if (selected) tint.copy(alpha = 0.16f) else androidx.compose.ui.graphics.Color.Transparent)
            .border(1.dp, tint.copy(alpha = if (selected) 1f else 0.5f), RoundedCornerShape(18.dp))
            .handCursor()
            .clickable(role = Role.Button, onClick = onClick)
            .semantics { this.selected = selected }
            .padding(horizontal = 12.dp, vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (warn) {
            Icon(Icons.Default.Warning, null, tint = tint, modifier = Modifier.size(14.dp))
            Spacer(Modifier.width(4.dp))
        }
        Text(text, style = MaterialTheme.typography.labelLarge, fontWeight = FontWeight.Bold, color = tint)
    }
}

@Composable
private fun AnswerGroup(answer: DayAnswer, people: List<PersonDayAnswer>, slots: List<AvailabilitySlot>, slotFilter: String?) {
    val on = LocalOnColours.current
    Column(Modifier.fillMaxWidth().testTag(DayAnswersTags.group(answer)), verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Text(
            stringResource(
                when (answer) {
                    DayAnswer.AVAILABLE -> Res.string.answers_group_available
                    DayAnswer.NO_ANSWER -> Res.string.answers_group_no_answer
                    DayAnswer.UNAVAILABLE -> Res.string.answers_group_unavailable
                },
                people.size
            ),
            style = MaterialTheme.typography.labelLarge,
            fontWeight = FontWeight.Bold,
            color = when (answer) {
                DayAnswer.AVAILABLE -> on.success
                DayAnswer.NO_ANSWER -> on.onSurfaceVariant
                DayAnswer.UNAVAILABLE -> on.error
            }
        )
        if (people.isEmpty()) {
            Text(stringResource(Res.string.answers_group_empty), style = MaterialTheme.typography.bodySmall, color = on.onSurfaceVariant)
        }
        people.forEach { person -> PersonRow(person, slots, slotFilter) }
    }
}

@Composable
private fun PersonRow(person: PersonDayAnswer, slots: List<AvailabilitySlot>, slotFilter: String?) {
    val on = LocalOnColours.current
    Row(Modifier.fillMaxWidth().testTag(DayAnswersTags.person(person.user.uid)), verticalAlignment = Alignment.CenterVertically) {
        UserAvatar(person.user.displayName, person.user.photoUrl, 32.dp)
        Spacer(Modifier.width(10.dp))
        Column(Modifier.weight(1f)) {
            Text(person.user.displayName, style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.SemiBold, color = on.onSurface)
            personDetail(person, slots, slotFilter)?.let {
                Text(it, style = MaterialTheme.typography.bodySmall, color = on.onSurfaceVariant)
            }
        }
    }
}

/** What the person said about the day: when they can ("Rano", "16:00–22:00", "cały dzień"), why not, and their comment. */
@Composable
private fun personDetail(person: PersonDayAnswer, slots: List<AvailabilitySlot>, slotFilter: String?): String? {
    val absence = person.absence
    val parts = mutableListOf<String>()
    when {
        absence != null -> parts += stringResource(Res.string.roster_picker_absent, absence.type.label())
        person.answer == DayAnswer.AVAILABLE -> {
            val whenText = whenAvailable(person.preference, slots)
            // For one slot, someone free only at other times says when they are.
            parts += if (slotFilter != null && slotFilter !in person.slotIds) stringResource(Res.string.answers_slot_only_other, whenText) else whenText
        }
    }
    person.preference?.comment?.takeIf { it.isNotBlank() }?.let { parts += "„$it”" }
    return parts.takeIf { it.isNotEmpty() }?.joinToString(" · ")
}

@Composable
private fun whenAvailable(preference: DayPreference?, slots: List<AvailabilitySlot>): String {
    val hours = preference?.hours.orEmpty()
    if (hours.isNotEmpty()) return hours.joinToString(", ") { "${it.start}–${it.end}" }
    val slot = preference?.preferredSlotId?.let { id -> slots.firstOrNull { it.id == id } }
    return slot?.label ?: stringResource(Res.string.answers_whole_day)
}
